import {
  FinanceProvider,
  FinanceRate,
  Job,
  Lead,
  Payment,
  WorkType,
} from '@kanda-libs/ks-frontend-services';

import { FormattedJob } from 'types';

import { FormattedRates } from './components/Rates/constants';

export interface LoanLimits {
  min: string;
  max: string;
  loanAmount: string;
}

export interface DepositLimits {
  min: string;
  max: string;
  depositAmount: string;
  isValid: boolean;
}

export const someRatesAreBuyNowPayLater = (
  formattedRates: FormattedRates,
  rateKey?: string,
): boolean => {
  const rates = rateKey
    ? formattedRates[rateKey]
    : Object.values(formattedRates).flat();

  return rates.some((rate) => rate.type === 'BUYNOW_PAYLATER');
};

export const checkHasSecondLine = (job?: FormattedJob | Job): boolean => {
  if (!job) return false;
  if (job.finance_status === 'not_applied') return false;
  const rates =
    job?.finance_options
      ?.filter((rate: FinanceRate) => rate.enabled)
      ?.filter((rate: FinanceRate) => rate.finance_types.includes('secondary'))
      ?.length || 0;
  return rates > 0;
};

export const getFinanceOptions = (
  job: FormattedJob | Job,
  lead?: Lead,
  filterBNPL?: boolean,
): FinanceRate[] => {
  if (job.finance_status === 'finance_not_approved' && checkHasSecondLine(job))
    return (
      job?.finance_options
        ?.filter((rate: FinanceRate) => rate.enabled)
        ?.filter((rate: FinanceRate) =>
          filterBNPL ? rate?.apr_type !== 'BUYNOW_PAYLATER' : true,
        ) || []
    );
  const rates = lead?.finance_options || job?.finance_options || [];
  return rates?.filter((rate: FinanceRate) =>
    filterBNPL ? rate?.apr_type !== 'BUYNOW_PAYLATER' : true,
  );
};

export const getFinanceOption = (
  job: FormattedJob,
  lead: Lead | undefined,
  duration: string,
  interest: number,
  provider: FinanceProvider,
): FinanceRate | undefined => {
  if (!job) return undefined;
  const quotedTo = job?.quoted_to;
  if (quotedTo && !lead) return undefined;
  const rates = getFinanceOptions(job, lead, false);
  if (!rates) return undefined;
  const selected = rates?.filter(
    (rate: FinanceRate) =>
      rate.apr === interest &&
      rate.provider === provider &&
      rate.duration === parseInt(duration, 10),
  );
  if (selected?.length === 0) return undefined;
  return selected?.[0];
};

const determineMax = (total: number, maxLoan: number, minDeposit: number) => {
  if (total >= maxLoan) {
    if (maxLoan + minDeposit <= total) return maxLoan;
    return maxLoan - minDeposit;
  }
  return total - minDeposit;
};

export const getLoanLimits = (
  job?: FormattedJob,
  lead?: Lead,
  filterBNPL = true,
): LoanLimits => {
  if (!job || !lead)
    return {
      min: '0',
      max: '100',
      loanAmount: '50',
    };
  const financeOptions = getFinanceOptions(job, lead, filterBNPL);
  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;
  const minDeposit = job?.deposit_value?.amount || 0;

  const maxDepositPct =
    (financeOptions
      ?.map((option: FinanceRate) => option.max_deposit_pct || 0)
      ?.sort((max1: number, max2: number) => max1 - max2)?.[0] || 7500) / 100;
  const maxDepositAmount = (maxDepositPct / 100) * jobTotal;
  const minLoanAmount =
    financeOptions
      ?.map((option: FinanceRate) => option.min_job_value || 0)
      ?.sort((min1: number, min2: number) => min1 - min2)?.[0] || 50000;
  const min = Math.max(minLoanAmount, maxDepositAmount);

  const maxLoan =
    lead?.max_credit_amount?.amount ||
    job?.finance_options
      ?.map((option: FinanceRate) => option.max_job_value || 0)
      ?.sort((max1: number, max2: number) => max2 - max1)?.[0] ||
    0;
  const max = determineMax(jobTotal, maxLoan, minDeposit);

  const loanAmount = Math.ceil((max + min) / 2);

  return {
    min: String(min),
    max: String(max),
    loanAmount: String(loanAmount),
  };
};

export const getDepositLimit = (
  rateName?: string,
  job?: FormattedJob | Job,
  lead?: Lead,
  filterBNPL = false,
): DepositLimits => {
  if (!rateName || !job)
    return {
      min: '0',
      max: '100',
      depositAmount: '50',
      isValid: false,
    };

  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;
  const jobDeposit = job?.deposit_value?.amount || 0;

  const rates = getFinanceOptions(job, lead, filterBNPL);
  const rate = rates?.find(
    (currentRate: FinanceRate) => currentRate.name === rateName,
  );

  if (!rate)
    return {
      min: '0',
      max: '100',
      depositAmount: '50',
      isValid: false,
    };

  const {
    min_job_value: minJobValue = 0,
    max_job_value: maxJobValue = 0,
    min_deposit_pct: minDepPct = 0,
    max_deposit_pct: maxDepPct = 0,
    max_deposit_value: maxDepVal = 0,
    min_deposit_value: minDepVal = 0,
    credit_line: creditLine = 0,
  } = rate;

  // Minimum deposit will be the maximum of the minimum deposit value for a rate,
  // the minimum deposit percentage, the set job deposit from the trader, the
  // credit line if the rate has a credit line associated (preapproved) or
  const jobMinDeposit = Math.max(
    minDepVal,
    jobTotal * (minDepPct / 10000),
    jobDeposit,
    creditLine !== 0 ? Math.max(jobTotal - creditLine, 0) : 0,
    jobTotal - maxJobValue,
  );

  // Maximum deposit will be the minimum of the maximum deposit value for a rate,
  // the maximum deposit percentage, or a fallback of 50% of the job value
  const jobMaxDeposit = Math.min(
    maxDepVal || Infinity,
    maxDepPct ? jobTotal * (maxDepPct / 10000) : Infinity,
    jobTotal - minJobValue,
    jobTotal * 0.5,
  );

  return {
    min: String(jobMinDeposit),
    max: String(jobMaxDeposit),
    depositAmount: String(jobDeposit),
    isValid: jobMinDeposit <= jobMaxDeposit,
  };
};

export const getHasApplicableLoans = (
  job?: FormattedJob,
  lead?: Lead,
  filterBNPL = true,
): boolean => {
  if (!job) return false;
  const rates = getFinanceOptions(job, lead, filterBNPL);
  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;
  const maxLoan =
    lead?.max_credit_amount?.amount ||
    rates
      ?.map((option: FinanceRate) => option.max_job_value || 0)
      ?.sort((max1: number, max2: number) => max2 - max1)?.[0] ||
    0;
  const maxDepositPct =
    (rates
      ?.map((option: FinanceRate) => option.max_deposit_pct || 0)
      ?.sort((max1: number, max2: number) => max1 - max2)?.[0] || 7500) / 100;
  const maxDepositAmount = (maxDepositPct / 100) * jobTotal;

  return jobTotal - maxDepositAmount - maxLoan <= 0;
};

export const formatter = (value: string): string => {
  const pounds = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(parseInt(value, 10) / 100);
  return pounds;
};

export const getLoanPayments = (
  job: FormattedJob | Job,
): Payment[] | undefined =>
  job?.payments
    ?.filter(
      (payment: Payment) => payment.payment_option.payment_method === 'loan',
    )
    ?.sort(
      (p1: Payment, p2: Payment) =>
        new Date(p2.metadata?.created_at || 0).getTime() -
        new Date(p1.metadata?.created_at || 0).getTime(),
    );

export const checkHasCredit = (
  job?: FormattedJob | Job,
  lead?: Lead,
): boolean => {
  if (!job || !lead) return false;
  const loanPayments = getLoanPayments(job);
  return Boolean(loanPayments?.[0]);
};

export const getWorkType = (
  job?: FormattedJob | Job,
  lead?: Lead,
): WorkType | undefined =>
  job?.work_type || lead?.lead_quote?.work_type || undefined;
